import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ArtifactCard from './ArtifactCard';

// Per-state presentation for the locked composer. Each lock code gets its own
// icon + short title so the three states read differently at a glance, instead
// of a single "blocked" look with only the text changing. The reason text
// (agreed Spanish copy from the backend) renders below as the subtitle.
function lockPresentation(code) {
    switch (code) {
        case 'locked_pending_approval':
            // Waiting — a clock, not a prohibition. Reversible.
            return {
                title: 'En revisión',
                icon: (
                    <>
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                    </>
                ),
            };
        case 'locked_approved':
            // Positive outcome — a check.
            return {
                title: 'PRD aprobado',
                icon: (
                    <>
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                        <polyline points="22 4 12 14.01 9 11.01" />
                    </>
                ),
            };
        case 'locked_rejected':
            // Terminal/negative — a blocked circle.
            return {
                title: 'Propuesta rechazada',
                icon: (
                    <>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                    </>
                ),
            };
        default:
            return {
                title: 'Conversación bloqueada',
                icon: (
                    <>
                        <circle cx="12" cy="12" r="10" />
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                    </>
                ),
            };
    }
}

export default function PrdChat({ messages, isStreaming, isLoading, error, onSend, onOpenArtifact, lockState }) {
    const lock = lockState || { locked: false };
    const [input, setInput] = useState('');
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);
    // Max visible height before the textarea stops growing and scrolls
    // internally (the "slider"/scrollbar for long text). ~6 lines.
    const MAX_TEXTAREA_HEIGHT = 140;

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    // Grow the textarea with its content up to MAX_TEXTAREA_HEIGHT; past that,
    // overflow-y lets the internal scrollbar take over for long text.
    const autoResize = () => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        const next = Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT);
        el.style.height = `${next}px`;
        el.style.overflowY = el.scrollHeight > MAX_TEXTAREA_HEIGHT ? 'auto' : 'hidden';
    };

    // Keep the caret in the textarea: refocus when streaming ends, since a
    // disabled input can't hold focus while the agent is responding.
    useEffect(() => {
        if (!isStreaming && !lock.locked && textareaRef.current) {
            textareaRef.current.focus();
        }
    }, [isStreaming, lock.locked]);

    // Recalculate height whenever the value is reset or set programmatically.
    useEffect(() => {
        autoResize();
    }, [input]);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!input.trim() || isStreaming) return;
        onSend(input.trim());
        setInput('');
        // Return the caret to the composer right after sending.
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    return (
        <div className="prd-chat">
            <div className="messages">
                {messages.length === 0 && (
                    <div className="empty-state">
                        <p>Describe tu proyecto y el agente te ayudará a generar un PRD completo.</p>
                    </div>
                )}

                {messages.map((msg) => {
                    // Manager comment: differentiated block, strip the technical marker
                    if (msg.kind === 'manager_comment') {
                        const visible = msg.content.replace(/^\[COMENTARIO_JEFE_DIRECTO\]\s*/, '');
                        return (
                            <div key={msg.id} className="manager-comment">
                                <span className="manager-comment__header">
                                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                                    Comentario de tu jefe directo
                                </span>
                                <div className="manager-comment__content">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {visible}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        );
                    }

                    // Don't render empty assistant message while streaming
                    if (msg.role === 'assistant' && !msg.content && !msg.artifact && isStreaming) {
                        return null;
                    }
                    return (
                        <div key={msg.id} className={`message message--${msg.role}`}>
                            <span className="message-author">
                                {msg.role === 'user' ? 'Tú' : 'Agente'}
                            </span>
                            <div className="message-content">
                                {msg.artifact ? (
                                    <ArtifactCard artifact={msg.artifact} onClick={onOpenArtifact} />
                                ) : (
                                    <div className="message-markdown">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {msg.content}
                                        </ReactMarkdown>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                {isStreaming && !isLoading && messages.some(m => m.role === 'user') && (
                    <div className="streaming-indicator" aria-live="polite">
                        <span className="dot dot--1"></span>
                        <span className="dot dot--2"></span>
                        <span className="dot dot--3"></span>
                    </div>
                )}

                {error && (
                    <div className="error-banner" role="alert">
                        Error: {error}
                    </div>
                )}

                <div ref={messagesEndRef} />
            </div>

            {lock.locked ? (() => {
                const { title, icon } = lockPresentation(lock.code);
                return (
                    <div
                        className={`chat-blocked chat-blocked--${lock.code || 'generic'}`}
                        role="status"
                    >
                        <svg
                            className="chat-blocked__icon"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            {icon}
                        </svg>
                        <span className="chat-blocked__body">
                            <span className="chat-blocked__title">{title}</span>
                            {/* Prefer the backend reason (agreed Spanish copy);
                                the module-level fallback covers an optimistic
                                lock before any 409 round-trip. */}
                            {lock.reason && (
                                <span className="chat-blocked__reason">{lock.reason}</span>
                            )}
                        </span>
                    </div>
                );
            })() : (
                <form className="chat-input" onSubmit={handleSubmit}>
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => {
                            setInput(e.target.value);
                            autoResize();
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder="Describe tu proyecto..."
                        disabled={isStreaming}
                        rows={2}
                    />
                    <button
                        type="submit"
                        className="btn-send"
                        disabled={isStreaming || !input.trim()}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13" />
                            <polygon points="22 2 15 22 11 13 2 9 22 2" />
                        </svg>
                    </button>
                </form>
            )}
        </div>
    );
}
